import useAuth from '@/features/auth/hooks/use-auth'
import {
  type Reservation,
  reservationCommandsSpeakV2,
  reservationCreateSpeaksV2
} from '../models/reservation-source'
import {
  authorizeReservation,
  createReservation,
  declineReservation,
  releaseReservation
} from '../services/inventory.services'
import { reservationsV2Service } from '../services/reservations-v2.service'

/**
 * Los cuatro comandos, ya resueltos a la versión que corresponde.
 *
 * Devuelve funciones y no un flag para que quien las use no tenga que ramificar: pide «liberá esta
 * reserva» y acá se decide con qué API. Ese `if` vive en UN lugar en vez de en cada diálogo.
 */
export function useReservationCommands() {
  const { auth } = useAuth()
  const accessToken = auth?.v2AccessToken ?? ''

  return {
    create: async (input: {
      propertyId: string
      propertyPublicId?: string
      /** Instante en que muere la retención. v1 lo recibe como su única fecha reservada. */
      endsAt: string
      reservedDates: string[]
      notes?: string
      /** Identifica la INTENCIÓN de reservar; un reintento la repite para no duplicar. */
      idempotencyKey?: string
    }) => {
      if (reservationCreateSpeaksV2(input.propertyPublicId)) {
        await reservationsV2Service.create(
          accessToken,
          {
            property: input.propertyPublicId as string,
            endsAt: input.endsAt,
            notes: input.notes
          },
          input.idempotencyKey
        )
        return
      }
      await createReservation({
        property: input.propertyId,
        reservedDates: input.reservedDates,
        notes: input.notes
      })
    },

    release: async (reservation: Reservation, notes: string) => {
      if (reservationCommandsSpeakV2(reservation.publicId)) {
        await reservationsV2Service.release(accessToken, reservation.publicId as string, notes)
        return
      }
      await releaseReservation(reservation.id, notes)
    },

    authorize: async (reservation: Reservation, notes?: string) => {
      if (reservationCommandsSpeakV2(reservation.publicId)) {
        await reservationsV2Service.authorize(accessToken, reservation.publicId as string, notes)
        return
      }
      await authorizeReservation(reservation.id, notes)
    },

    decline: async (reservation: Reservation, notes: string) => {
      if (reservationCommandsSpeakV2(reservation.publicId)) {
        await reservationsV2Service.decline(accessToken, reservation.publicId as string, notes)
        return
      }
      await declineReservation(reservation.id, notes)
    }
  }
}
